// src/diagrams/routing/useAdvancedRouting.js - Hook for integrating advanced routing with React Flow

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { EdgeRoutingSystem } from './EdgeRoutingSystem';

/**
 * Custom hook for advanced edge routing in React Flow
 */
export const useAdvancedRouting = (options = {}) => {
  // Key the routing system on the config *value*, not the object reference. Callers
  // typically pass a freshly-built config object every render, which previously
  // rebuilt the routing system (and churned processEdgesWithRouting's identity) on
  // every render. Serializing the options stabilizes it to actual config changes.
  const optionsKey = JSON.stringify(options);
  const routingSystem = useMemo(() => new EdgeRoutingSystem({
    gridSize: 10,
    nodePadding: 35,
    enableBundling: true,
    bundleThreshold: 0.7,
    maxBundleSize: 5,
    crossingPenalty: 15,
    lengthPenalty: 1,
    turnPenalty: 3,
    proximityPenalty: 8,
    routingPolicy: 'SMART',
    ...options
  }), [optionsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isRouting, setIsRouting] = useState(false);
  const [routingStats, setRoutingStats] = useState({
    totalEdges: 0,
    routedEdges: 0,
    bundledEdges: 0,
    processingTime: 0
  });

  /**
   * Process edges with advanced routing
   */
  const processEdgesWithRouting = useCallback(async (nodes, edges, layoutDirection = 'TB') => {
    if (!nodes || !edges || nodes.length === 0 || edges.length === 0) {
      return edges;
    }

    setIsRouting(true);
    const startTime = performance.now();

    try {
      const routedEdges = routingSystem.routeEdges(nodes, edges, layoutDirection);
      
      const endTime = performance.now();
      setRoutingStats({
        totalEdges: edges.length,
        routedEdges: routedEdges.length,
        bundledEdges: 0, // Will be calculated by edge components
        processingTime: endTime - startTime
      });

      return routedEdges;
    } catch (error) {
      console.error('Edge routing failed:', error);
      return edges.map(edge => ({
        ...edge,
        data: {
          ...edge.data,
          nodes,
          layoutDirection
        }
      }));
    } finally {
      setIsRouting(false);
    }
  }, [routingSystem]);

  /**
   * Debounced version for real-time updates
   */
  const debouncedProcessEdges = useDebounce(processEdgesWithRouting, 500);

  return {
    processEdgesWithRouting,
    debouncedProcessEdges,
    isRouting,
    routingStats,
    routingSystem
  };
};

// Note: Web Worker implementation for edge routing would require serializable routing system
/**
 * Debounce hook for performance optimization.
 * Invokes the *latest* callback via a ref so it never closes over a stale routing
 * system when the config changes.
 */
function useDebounce(callback, delay) {
  const callbackRef = useRef(callback);
  useEffect(() => { callbackRef.current = callback; }, [callback]);

  const [debouncedCallback] = useState(() => {
    let timeoutId;
    return (...args) => {
      return new Promise((resolve, reject) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          try {
            const result = await callbackRef.current(...args);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, delay);
      });
    };
  });

  return debouncedCallback;
}

/**
 * React Flow integration utilities
 */
export class ReactFlowAdvancedRoutingIntegration {
  /**
   * Convert routing results back to React Flow edge format
   */
  static convertPathsToReactFlowEdges(originalEdges, routedPaths, layoutDirection) {
    return originalEdges.map(edge => {
      const routedPath = routedPaths.get(edge.id);
      
      if (!routedPath || routedPath.length < 2) {
        return {
          ...edge,
          data: {
            ...edge.data,
            layoutDirection,
            routingFailed: true
          }
        };
      }

      // Determine edge type based on original type and routing result
      let edgeType = edge.type || 'advancedEdge';
      
      // Map existing types to advanced types
      const typeMapping = {
        'defaultEdge': 'advancedEdge',
        'routedEdge': 'advancedEdge',
        'errorEdge': 'advancedErrorEdge',
        'routedErrorEdge': 'advancedErrorEdge',
        'conditionEdge': 'advancedConditionEdge',
        'routedConditionEdge': 'advancedConditionEdge'
      };

      if (typeMapping[edgeType]) {
        edgeType = typeMapping[edgeType];
      }

      // Check if edge is part of a bundle
      const bundleInfo = this.getBundleInfo(edge.data);
      if (bundleInfo.isBundled) {
        edgeType = 'bundledEdge';
      }

      return {
        ...edge,
        type: edgeType,
        data: {
          ...edge.data,
          routedPath,
          layoutDirection,
          routingSuccess: true,
          ...bundleInfo
        }
      };
    });
  }

  static getBundleInfo(edgeData) {
    if (edgeData?.bundleId) {
      return {
        isBundled: true,
        bundleId: edgeData.bundleId,
        bundleSize: edgeData.bundleSize || 1,
        bundleIndex: edgeData.bundleIndex || 0,
        bundleOffset: this.calculateBundleOffset(edgeData.bundleIndex, edgeData.bundleSize)
      };
    }
    
    return { isBundled: false };
  }

  static calculateBundleOffset(index, size) {
    if (size <= 1) return 0;
    
    const spacing = 8; // pixels between bundled edges
    const center = (size - 1) / 2;
    return (index - center) * spacing;
  }

  /**
   * Performance monitoring utilities
   */
  static createPerformanceMonitor() {
    const metrics = {
      routingTimes: [],
      edgeCounts: [],
      bundleEfficiency: []
    };

    return {
      recordRouting: (time, edgeCount, bundleCount) => {
        metrics.routingTimes.push(time);
        metrics.edgeCounts.push(edgeCount);
        metrics.bundleEfficiency.push(bundleCount / edgeCount);
        
        // Keep only last 50 measurements
        if (metrics.routingTimes.length > 50) {
          metrics.routingTimes.shift();
          metrics.edgeCounts.shift();
          metrics.bundleEfficiency.shift();
        }
      },
      
      getAverageTime: () => {
        return metrics.routingTimes.length > 0 
          ? metrics.routingTimes.reduce((a, b) => a + b) / metrics.routingTimes.length 
          : 0;
      },
      
      getMetrics: () => ({ ...metrics })
    };
  }
}

/**
 * Configuration presets for different use cases
 */
export const RoutingPresets = {
  // Fast routing for real-time interaction
  PERFORMANCE: {
    preset: 'PERFORMANCE',
    gridSize: 20,
    nodePadding: 30,
    enableBundling: false,
    optimizationIterations: 1,
    routingPolicy: 'AS_NEEDED'
  },

  // Balanced quality and performance
  BALANCED: {
    preset: 'BALANCED',
    gridSize: 10,
    nodePadding: 35,
    crossingPenalty: 15,
    turnPenalty: 3,
    proximityPenalty: 8,
    enableBundling: true,
    bundleThreshold: 0.7,
    optimizationIterations: 2,
    routingPolicy: 'SMART'
  },

  // Maximum quality for final presentations
  QUALITY: {
    preset: 'QUALITY',
    gridSize: 8,
    nodePadding: 40,
    enableBundling: true,
    bundleThreshold: 0.6,
    maxBundleSize: 8,
    optimizationIterations: 4,
    crossingPenalty: 25,
    turnPenalty: 4,
    proximityPenalty: 10,
    routingPolicy: 'ALWAYS'
  },

  // Optimized for large graphs
  LARGE_GRAPH: {
    preset: 'LARGE_GRAPH',
    gridSize: 15,
    nodePadding: 30,
    enableBundling: true,
    bundleThreshold: 0.8,
    maxBundleSize: 10,
    optimizationIterations: 1,
    routingPolicy: 'AS_NEEDED'
  }
};

/**
 * Edge routing configuration component - Fixed to properly trigger changes
 */
export const RoutingConfiguration = ({ onConfigChange, currentConfig = {} }) => {
  const [config, setConfig] = useState({
    preset: 'BALANCED',
    ...currentConfig
  });

  // Update local config when currentConfig prop changes
  useEffect(() => {
    setConfig(prev => ({
      ...prev,
      ...currentConfig
    }));
  }, [currentConfig]);

  const handlePresetChange = (preset) => {
    const newConfig = {
      ...RoutingPresets[preset],
      preset
    };
    setConfig(newConfig);
    
    // Immediately notify parent of the change
    if (onConfigChange) {
      console.log('Preset changed to:', preset, newConfig);
      onConfigChange(newConfig);
    }
  };

  const handleCustomChange = (key, value) => {
    const newConfig = {
      ...config,
      [key]: value,
      preset: 'CUSTOM'
    };
    setConfig(newConfig);
    
    // Immediately notify parent of the change
    if (onConfigChange) {
      console.log('Custom config changed:', key, value, newConfig);
      onConfigChange(newConfig);
    }
  };

  return (
    <div style={styles.container}>
      <h4 style={styles.title}>Edge Routing Configuration</h4>
      
      <div style={styles.presetSection}>
        <label style={styles.label}>Preset:</label>
        <select 
          style={styles.select}
          value={config.preset || 'BALANCED'} 
          onChange={(e) => handlePresetChange(e.target.value)}
        >
          <option value="PERFORMANCE">Performance</option>
          <option value="BALANCED">Balanced</option>
          <option value="QUALITY">Quality</option>
          <option value="LARGE_GRAPH">Large Graph</option>
          <option value="CUSTOM">Custom</option>
        </select>
      </div>

      {config.preset === 'CUSTOM' && (
        <div style={styles.customControls}>
          <div style={styles.controlRow}>
            <label style={styles.controlLabel}>Grid Size:</label>
            <input 
              style={styles.range}
              type="range" 
              min="10" 
              max="30" 
              value={config.gridSize || 15}
              onChange={(e) => handleCustomChange('gridSize', parseInt(e.target.value))}
            />
            <span style={styles.valueDisplay}>{config.gridSize || 15}</span>
          </div>
          
          <div style={styles.controlRow}>
            <label style={styles.controlLabel}>Node Padding:</label>
            <input 
              style={styles.range}
              type="range" 
              min="15" 
              max="40" 
              value={config.nodePadding || 25}
              onChange={(e) => handleCustomChange('nodePadding', parseInt(e.target.value))}
            />
            <span style={styles.valueDisplay}>{config.nodePadding || 25}</span>
          </div>
          
          <div style={styles.controlRow}>
            <label style={styles.controlLabel}>Enable Bundling:</label>
            <input 
              style={styles.checkbox}
              type="checkbox" 
              checked={config.enableBundling ?? true}
              onChange={(e) => handleCustomChange('enableBundling', e.target.checked)}
            />
          </div>
          
          <div style={styles.controlRow}>
            <label style={styles.controlLabel}>Bundle Threshold:</label>
            <input 
              style={styles.range}
              type="range" 
              min="0.5" 
              max="0.9" 
              step="0.1"
              value={config.bundleThreshold || 0.7}
              onChange={(e) => handleCustomChange('bundleThreshold', parseFloat(e.target.value))}
            />
            <span style={styles.valueDisplay}>{config.bundleThreshold || 0.7}</span>
          </div>
        </div>
      )}

      <div style={styles.description}>
        <strong>Current Settings:</strong><br/>
        {config.preset === 'PERFORMANCE' && 'Optimized for speed with minimal processing overhead'}
        {config.preset === 'BALANCED' && 'Good balance of quality and performance for most use cases'}
        {config.preset === 'QUALITY' && 'Maximum quality routing with detailed optimization'}
        {config.preset === 'LARGE_GRAPH' && 'Optimized for graphs with many nodes and edges'}
        {config.preset === 'CUSTOM' && 'Custom configuration with user-defined parameters'}
      </div>
    </div>
  );
};

// Styles object for the configuration component
const styles = {
  container: {
    padding: '16px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    margin: '8px 0',
    backgroundColor: '#fafafa'
  },
  title: {
    margin: '0 0 12px 0',
    color: '#333',
    fontSize: '14px'
  },
  presetSection: {
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  label: {
    display: 'inline-block',
    width: '80px',
    fontWeight: 'bold',
    fontSize: '12px'
  },
  select: {
    padding: '4px 8px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    fontSize: '12px'
  },
  customControls: {
    marginBottom: '12px'
  },
  controlRow: {
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  controlLabel: {
    width: '120px',
    fontSize: '12px',
    fontWeight: '500'
  },
  range: {
    flex: 1,
    maxWidth: '120px'
  },
  valueDisplay: {
    minWidth: '30px',
    fontSize: '12px',
    color: '#666'
  },
  checkbox: {
    width: '16px',
    height: '16px'
  },
  description: {
    fontSize: '11px',
    color: '#666',
    fontStyle: 'italic',
    lineHeight: '1.4',
    marginTop: '8px',
    padding: '8px',
    backgroundColor: '#f0f0f0',
    borderRadius: '3px'
  }
};

// CSS for the configuration component (if needed as a string)
export const routingConfigStyles = `
.routing-configuration {
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin: 8px 0;
  background-color: #fafafa;
}

.routing-configuration h4 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 14px;
}

.preset-selection {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preset-selection label {
  display: inline-block;
  width: 80px;
  font-weight: bold;
  font-size: 12px;
}

.preset-selection select {
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
}

.custom-controls > div {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-controls label {
  width: 120px;
  font-size: 12px;
  font-weight: 500;
}

.custom-controls input[type="range"] {
  flex: 1;
  max-width: 120px;
}

.custom-controls span {
  min-width: 30px;
  font-size: 12px;
  color: #666;
}

.custom-controls input[type="checkbox"] {
  width: 16px;
  height: 16px;
}
`;